import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import React, { Component, ErrorInfo, ReactNode } from 'react';

import { ActionButton, ButtonGroup, ErrorBoundaryContainer, ErrorCode, ErrorContent, ErrorDetails, ErrorIcon, ErrorMessage, ErrorStack, ErrorTitle } from './ErrorBoundary.styled';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorCount: number;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
      errorCount: 0,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Log to external service in production
    if (process.env.NODE_ENV === 'production') {
      // Here you could send to Sentry, LogRocket, etc.
      ErrorBoundary.logErrorToService(error, errorInfo);
    }

    // Update state with error details
    this.setState((prevState) => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));
  }

  static logErrorToService(error: Error, errorInfo: ErrorInfo): void {
    // Implement your error logging service here
    // Example: Sentry.captureException(error, { extra: errorInfo });
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    // Send to your logging endpoint
    // eslint-disable-next-line no-console
    console.error('Error logged to service:', errorData);
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    });
  };

  static handleReload(): void {
    window.location.reload();
  }

  static handleGoHome(): void {
    window.location.href = '/';
  }

  render(): ReactNode {
    const { hasError, error, errorInfo, errorCount } = this.state;
    const { children, fallback } = this.props;

    // If there's an error and too many retries, force reload
    if (errorCount > 3) {
      window.location.reload();
      return null;
    }

    if (hasError && error) {
      // Custom fallback if provided
      if (fallback) {
        return fallback as ReactNode;
      }

      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <ErrorBoundaryContainer>
          <ErrorContent>
            <ErrorIcon>
              <AlertTriangle size={48} />
            </ErrorIcon>

            <ErrorTitle>Oops! Something went wrong</ErrorTitle>

            <ErrorMessage>
              We're sorry, but something unexpected happened. Please try refreshing the page or contact support if the problem persists.
            </ErrorMessage>

            {isDevelopment && error && (
              <ErrorDetails>
                <ErrorCode>
                  <strong>Error:</strong>
                  {' '}
                  {error.message}
                </ErrorCode>

                {error.stack && (
                  <ErrorStack>
                    <strong>Stack trace:</strong>
                    <pre>{error.stack}</pre>
                  </ErrorStack>
                )}

                {errorInfo && errorInfo.componentStack && (
                  <ErrorStack>
                    <strong>Component stack:</strong>
                    <pre>{errorInfo.componentStack}</pre>
                  </ErrorStack>
                )}
              </ErrorDetails>
            )}

            <ButtonGroup>
              <ActionButton onClick={this.handleReset} $variant="primary">
                <RefreshCw size={18} />
                Try Again
              </ActionButton>

              <ActionButton onClick={ErrorBoundary.handleReload} $variant="secondary">
                <RefreshCw size={18} />
                Reload Page
              </ActionButton>

              <ActionButton onClick={ErrorBoundary.handleGoHome} $variant="secondary">
                <Home size={18} />
                Go Home
              </ActionButton>
            </ButtonGroup>

            {errorCount > 1 && (
              <ErrorMessage>
                Error occurred
                {' '}
                {errorCount}
                {' '}
                times. The page will auto-reload after 3 attempts.
              </ErrorMessage>
            )}
          </ErrorContent>
        </ErrorBoundaryContainer>
      );
    }

    return children;
  }
}
